import clsx from 'clsx';
import type { JSX } from 'react';
import { FormField } from '@/components/form';
import { PUBLIC_READINESS_LABELS } from '@/components/public/properties';
import type { UnitAvailabilityStatus, UnitPossessionTimeline } from '@/lib/types/property';
import {
  UNIT_STATUS_OPTIONS,
  UNIT_POSSESSION_TIMELINE_OPTIONS,
  type UnitFormErrors,
  type UnitFormState,
} from './unitFormState';

interface UnitAvailabilitySectionProps {
  formState: UnitFormState;
  errors: UnitFormErrors;
  disabled: boolean;
  isUploadingHero: boolean;
  setFormState: React.Dispatch<React.SetStateAction<UnitFormState>>;
  clearFieldError: (field: keyof UnitFormState) => void;
}

const inputClass =
  'rounded-2xl border border-surface-subtle px-4 py-2.5 text-base text-text-primary shadow-sm transition focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 disabled:cursor-not-allowed disabled:opacity-60 md:py-2 md:text-sm';

export function UnitAvailabilitySection({
  formState,
  errors,
  disabled,
  isUploadingHero,
  setFormState,
  clearFieldError,
}: UnitAvailabilitySectionProps): JSX.Element {
  return (
    <>
      <section className="grid gap-4 md:grid-cols-2">
        <FormField
          id="unit-availability"
          label="Move-in readiness"
          description="Controls whether the unit is ready for immediate occupancy or flexible move-in timing."
          error={errors.possessionTimeline}
          hint="This only controls move-in timing copy. It does not control public website visibility or realtor outreach emails."
          required
        >
          <select
            value={formState.possessionTimeline}
            onChange={(event) => {
              setFormState((prev) => ({
                ...prev,
                possessionTimeline: event.target.value as UnitPossessionTimeline,
              }));
              clearFieldError('possessionTimeline');
            }}
            disabled={disabled || isUploadingHero}
            className={clsx(inputClass, 'bg-white')}
          >
            {UNIT_POSSESSION_TIMELINE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option === 'negotiable' ? 'Negotiable' : 'Immediate'}
              </option>
            ))}
          </select>
        </FormField>

        <FormField
          id="unit-availability-date"
          label="Earliest move-in date"
          description="Optional. When set, public listings show the earliest specific move-in date."
          error={errors.availabilityStart}
          hint="Example: 2026-08-01"
        >
          <input
            type="date"
            value={formState.availabilityStart}
            onChange={(event) => {
              setFormState((prev) => ({
                ...prev,
                availabilityStart: event.target.value,
              }));
              clearFieldError('availabilityStart');
            }}
            disabled={disabled || isUploadingHero}
            className={inputClass}
          />
        </FormField>

        <FormField
          id="unit-readiness"
          label="Unit availability status"
          description="Tracks the unit's current leasing status for internal and public detail views."
          error={errors.availabilityStatus}
          hint="This status does not control whether the unit appears on the public website."
          required
        >
          <select
            value={formState.availabilityStatus}
            onChange={(event) => {
              setFormState((prev) => ({
                ...prev,
                availabilityStatus: event.target.value as UnitAvailabilityStatus,
              }));
              clearFieldError('availabilityStatus');
            }}
            disabled={disabled || isUploadingHero}
            className={clsx(inputClass, 'bg-white')}
          >
            {UNIT_STATUS_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {PUBLIC_READINESS_LABELS[option]}
              </option>
            ))}
          </select>
        </FormField>

        <FormField
          id="unit-display-public"
          label="Display on public website?"
          description="Choose whether this unit appears on the public website."
          error={errors.displayOnPublicWebsite}
          hint="Yes shows this unit publicly regardless of move-in timing, move-in date, or availability status."
          required
        >
          <select
            value={formState.displayOnPublicWebsite ? 'yes' : 'no'}
            onChange={(event) => {
              setFormState((prev) => ({
                ...prev,
                displayOnPublicWebsite: event.target.value === 'yes',
              }));
              clearFieldError('displayOnPublicWebsite');
            }}
            disabled={disabled || isUploadingHero}
            className={clsx(inputClass, 'bg-white')}
          >
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </FormField>
      </section>

      <section className="grid gap-4">
        <FormField
          id="unit-contact"
          label="Contact email"
          description="Used as the public contact email for inquiries and showing requests. Example: leasing@strashindevelopments.com."
          error={errors.contactEmail}
          required
        >
          <input
            type="email"
            autoComplete="email"
            value={formState.contactEmail}
            onChange={(event) => {
              setFormState((prev) => ({ ...prev, contactEmail: event.target.value }));
              clearFieldError('contactEmail');
            }}
            disabled={disabled || isUploadingHero}
            className={inputClass}
          />
        </FormField>
      </section>
    </>
  );
}
